/**
 * Shared Tailwind className constants for the guide page render blocks.
 *
 * These are presentational class strings only — no install copy lives here.
 * Extracted from app/guide/page.tsx so the page body and the new
 * components/guide/* render components can share identical styling without
 * duplicating long arbitrary-value class strings.
 */

import { TESLA_FACTS } from "@/lib/tesla-facts";
import type { CSSProperties } from "react";

// text-brand (--tls-brand) is the FILL token: ~3.2:1 on the dark canvas,
// a fail for 4.5:1 body text. These are inline text links, so they use the
// accent-as-text token instead (see components/ui/button.tsx's link variant
// for the same fix and app/globals.css's --tls-brand-ink).
export const inlineTouchLinkClass =
  "relative text-[color:var(--tls-brand-ink)] hover:text-[color:var(--tls-brand-ink-hover)] underline underline-offset-2 transition-colors before:absolute before:-inset-x-2 before:-inset-y-1.5 before:content-['']";

export const inlineCodeClass =
  "rounded px-2 py-1 text-sm font-mono break-words text-[var(--tls-fg-strong)] bg-[var(--tls-surface-elevated)] border border-[color:var(--tls-border-subtle)]";

// factCodeChipClass: the bordered-pill treatment for a short fact/path string
// inline in body copy (e.g. `LockChime.wav`, `Boombox/LockChime.wav`,
// `Boombox`). Duplicated across app/lockchime, app/lockchime-wav,
// app/horn-sounds, app/custom-chime, app/boombox-sounds,
// app/tesla-unlock-sound with slightly inconsistent radius tokens
// (--radius-2 vs --ls-radius-0) and none of them wrapped. At 200% root
// font-size (WCAG 1.4.4/1.4.10) an unbroken chip like
// TESLA_FACTS.usb.path ("Boombox/LockChime.wav") pushes the viewport wider
// than 390px. break-words fixes today's strings; centralizing here means a
// future longer fact string (a catalog update, a new USB path) is fixed by
// construction instead of by the next overflow scan. --ls-radius-0 is the
// canonical token (Plate & Hairline has no rounded corners on content); the
// lockchime pages' --radius-2 was a pre-P&H leftover, corrected here.
export const factCodeChipClass =
  "break-words rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] px-1.5 py-0.5 text-sm text-[var(--tls-brand-ink)]";

// factCodeChipCompactClass: same construct, smaller/plainer variant used for
// a path string set at text-xs without the brand-ink ~coloring (the
// USB-path quick-start step chips). Same break-words fix, same rationale.
export const factCodeChipCompactClass =
  "break-words rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] px-1 py-0.5 font-mono text-xs";

// Plate & Hairline §02.1 (Button): exactly one primary treatment — accent
// fill, never ink fill. Before this fix, primaryCtaClass filled with
// --tls-fg-strong (ink) while every other primary CTA on the site
// (components/ui/button.tsx's default variant, /download's two CTAs) fills
// with the accent (--tls-brand). Two primary treatments on one page (e.g.
// /download) reads as two different systems. Standardizing here on accent
// propagates to every consumer (app/guide, app/install/model-y-*, app/faq,
// app/[lang]/*) without touching call sites. See Wave 0 report for the full
// reasoning — this does NOT change what --tls-brand resolves to (still the
// bronze accent; PRODUCT.md's blue accent swap is a separate, not-yet-
// authorized migration decision).
// break-all + max-w-full: some locale strings are a single unbroken compound
// word (e.g. German "Installationsanleitung") with no space/hyphen to wrap
// on. These CTAs sit in a `flex flex-wrap` row with no width cap, so an
// unconstrained inline-flex item just grows to fit its content on one line
// instead of wrapping — max-w-full bounds it to the row's available width.
// overflow-wrap:break-word (Tailwind's break-words) alone still left a ~9px
// residual overflow (it avoids breaking when it can, so it left a
// near-full-width remainder that didn't quite fit); word-break:break-all
// (`break-all`) breaks at any character, which guarantees the fit. Only
// matters for a single unbroken run this long, so short English CTAs (which
// already wrap on spaces) render identically either way. Without this, the
// CTA pill forced the viewport wider than 390px at 200% root font-size on
// /de (WCAG 1.4.4/1.4.10).
//
// 2026-08-15: both strings now mirror components/ui/button.tsx byte-for-byte
// on skin, target, press and focus; they are the <Button> primary and
// secondary variants for the ~40 `<Link>`/`<a>` call sites that concatenate
// their own padding (so buttonVariants() itself, which carries a fixed
// h-11/px-8, cannot be dropped in without a call-site sweep). Anything that
// changes in the primitive's skin must change here too:
//   primary   = accent fill, brand-fg text, 700, hover = brand-hover
//   secondary = transparent, 1px INK border, ink text, hover inverts to ink
//               fill (the button.tsx `secondary`/`outline` class string)
//   both      = 44px min target, pill radius, press scale .97 + opacity .85
//               (motion-safe), .tlsr-focus DS ring
// The previous secondary was a tinted grey pill (ground-2 fill + hairline
// border + fg-muted), a third secondary treatment next to /download's
// outline pill and consent's tinted pill. One secondary now.
const ctaBaseClass =
  "inline-flex max-w-full min-h-[var(--tls-touch-min)] items-center justify-center gap-2 break-all rounded-[var(--tls-radius-pill)] border no-underline [transition:background-color_var(--motion-duration-standard)_var(--motion-ease-standard),border-color_var(--motion-duration-standard)_var(--motion-ease-standard),color_var(--motion-duration-standard)_var(--motion-ease-standard),scale_var(--ls-motion-instant)_var(--ls-ease-instant),opacity_var(--ls-motion-instant)_var(--ls-ease-instant)] motion-safe:active:scale-[0.97] motion-safe:active:opacity-85 tlsr-focus";

export const primaryCtaClass =
  `${ctaBaseClass} border-transparent bg-[var(--tls-brand)] text-[var(--tls-brand-fg)] font-bold hover:bg-[var(--tls-brand-hover)] motion-reduce:active:bg-[var(--tls-brand-hover)]`;

export const secondaryCtaClass =
  `${ctaBaseClass} border-[color:var(--tls-fg-strong)] bg-transparent text-[var(--tls-fg-strong)] font-semibold hover:bg-[var(--tls-fg-strong)] hover:text-[var(--tls-canvas)] motion-reduce:active:bg-[var(--tls-fg-strong)] motion-reduce:active:text-[var(--tls-canvas)]`;

/**
 * Rot-prone display dates, hoisted so the guide page and the compatibility
 * matrix reference one place instead of scattering hardcoded date strings
 * across JSX. Update these when the guide/matrix is re-verified.
 */
export const GUIDE_UPDATED_NOTE =
  "Updated 20 Apr 2026 for the Model Y (Juniper) install flow.";
export const MATRIX_VERIFIED_LABEL = "Last verified: 17 Apr 2026.";

/**
 * Shared inline-style blocks for the hero <h1> and section <h2> clamp() type
 * treatments. These live as style objects (not Tailwind classes) because the
 * clamp() font-size scale is not expressible through the existing type tokens
 * without adding new global tokens (out of scope for this cluster). Extracted
 * so the guide page and its install variants stop duplicating the literals.
 */
export const guideHeroH1Style: CSSProperties = {
  fontSize: "clamp(2.4rem, 5vw, 4rem)",
  fontWeight: 700,
  letterSpacing: "-0.035em",
};
export const installHeroH1Style: CSSProperties = {
  fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)",
  fontWeight: 700,
  letterSpacing: "-0.035em",
};
export const installSectionH2Style: CSSProperties = {
  fontSize: "clamp(1.5rem, 2.8vw, 2rem)",
  letterSpacing: "-0.02em",
};
